import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import { selectIsAuthenticated, selectIsOrgOwner } from "../../authentication/authenticationSlice";
import { selectOrganization } from "../organization-slice";
import { ROUTES } from "../../../routes";

/** Routes the first-run step never interrupts: auth flows, legal pages, the portal, invitations. */
const EXEMPT_PREFIXES: string[] = [
  ROUTES.WELCOME_KIND,
  ROUTES.LOGIN,
  ROUTES.REGISTER,
  ROUTES.AUTHORIZE_REDIRECT,
  ROUTES.CHOOSE_CONTEXT,
  ROUTES.EMAIL_CONFIRMATION,
  ROUTES.EMAIL_CONFIRMED,
  ROUTES.RESET_PASSWORD,
  ROUTES.RESET_PASSWORD_EMAIL_SENT,
  ROUTES.TERMS_OF_SERVICE,
  ROUTES.PRIVACY_POLICY,
  "/legal",
  ROUTES.INVITATION,
  "/partner",
  ROUTES.PORTAL,
];

/** True when this pathname should send the owner to the first-run kind question. */
export const shouldAskKind = (
  pathname: string,
  isAuthenticated: boolean,
  isOwner: boolean,
  kindChosenAt: string | null | undefined
): boolean =>
  isAuthenticated &&
  isOwner &&
  kindChosenAt === null &&
  !EXEMPT_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

/**
 * Sends an Owner whose organization never answered "what kind of school do you run?"
 * to /welcome/kind once. Admins and Instructors never see it; existing organizations
 * were backfilled as answered.
 */
const KindChoiceGate = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isOwner = useAppSelector(selectIsOrgOwner);
  const organization = useAppSelector(selectOrganization);
  const ask = shouldAskKind(pathname, isAuthenticated, isOwner, organization?.kindChosenAt);

  useEffect(() => {
    if (ask) navigate(ROUTES.WELCOME_KIND, { replace: true });
  }, [ask, navigate]);

  return null;
};

export default KindChoiceGate;
